import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  LocationOn,
  Work,
  PostAdd,
  TrendingUp
} from '@mui/icons-material';
import { dashboardAPI } from '../services/api';

const StatCard = ({ title, value, icon, color, gradient }) => (
  <Card 
    sx={{ 
      height: '100%',
      background: gradient,
      color: 'white',
      position: 'relative',
      overflow: 'hidden',
      borderRadius: 2,
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    }}
  >
    <CardContent sx={{ position: 'relative', zIndex: 1, p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1,
            bgcolor: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {React.cloneElement(icon, { sx: { fontSize: 20 } })}
        </Box>
        <Typography variant="h4" fontWeight="700" sx={{ lineHeight: 1 }}>
          {value}
        </Typography>
      </Box>
      <Typography variant="caption" sx={{ opacity: 0.9, fontWeight: 500, fontSize: '0.75rem' }}>
        {title}
      </Typography>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await dashboardAPI.getStats();
      setStats(response.data.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load dashboard data');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="700" gutterBottom>
          Dashboard Overview
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Welcome back! Here's what's happening with your job postings.
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Locations"
            value={stats?.overview?.totalLocations || 0}
            icon={<LocationOn />}
            gradient="linear-gradient(135deg, #8B1538 0%, #A91D47 100%)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Positions"
            value={stats?.overview?.totalPositions || 0}
            icon={<Work />}
            gradient="linear-gradient(135deg, #B8A369 0%, #C9B57E 100%)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Jobs"
            value={stats?.overview?.totalJobs || 0}
            icon={<PostAdd />}
            gradient="linear-gradient(135deg, #8B1538 0%, #B8A369 100%)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Inactive Jobs"
            value={stats?.overview?.totalInactiveJobs || 0}
            icon={<TrendingUp />}
            gradient="linear-gradient(135deg, #9A8754 0%, #6B0F2A 100%)"
          />
        </Grid>
      </Grid>

      {/* Recent Jobs */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mb: 3 }}>
              Recently Added Jobs
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Job Title</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Position</TableCell>
                    <TableCell>Type</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats?.recentJobs?.length > 0 ? (
                    stats.recentJobs.map((job) => (
                      <TableRow 
                        key={job._id}
                        sx={{
                          '&:hover': {
                            bgcolor: 'action.hover'
                          }
                        }}
                      >
                        <TableCell sx={{ fontWeight: 500 }}>{job.title || 'N/A'}</TableCell>
                        <TableCell>
                          {job.locations && job.locations.length > 0 ? (
                            <>
                              {job.locations[0]?.name || 'N/A'}
                              {job.locations[0]?.city && `, ${job.locations[0].city}`}
                              {job.locations.length > 1 && ` +${job.locations.length - 1}`}
                            </>
                          ) : (
                            'N/A'
                          )}
                        </TableCell>
                        <TableCell>{job.position?.title || 'N/A'}</TableCell>
                        <TableCell>
                          {job.employmentType ? (
                            <Chip 
                              label={job.employmentType} 
                              size="small" 
                              sx={{
                                background: 'linear-gradient(135deg, #8B1538 0%, #B8A369 100%)',
                                color: 'white',
                                fontWeight: 500
                              }}
                            />
                          ) : (
                            'N/A'
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No jobs posted yet
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
